/** Presentation only. Decimal strings never pass through binary floating point. */
export function currencyDecimals(currency = "USD"): number {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits!;
}
function decimal(value: number | string): { negative: boolean; whole: string; fraction: string } | undefined {
  const match = String(value).trim().match(/^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i);
  if (!match || !(match[2] || match[3])) return;
  const digits = (match[2] || "") + (match[3] || "");
  const point = (match[2]?.length || 0) + Number(match[4] || 0);
  if (Math.abs(point) > 1000) return;
  const whole = (point <= 0 ? "0" : digits.slice(0, point).padEnd(point, "0")).replace(/^0+(?=\d)/, "");
  const fraction = point < 0 ? "0".repeat(-point) + digits : digits.slice(point);
  return { negative: match[1] === "-", whole, fraction };
}
function rounded(value: number | string, digits: number): bigint | undefined {
  const d = decimal(value); if (!d) return;
  const magnitude = BigInt(d.whole + d.fraction.slice(0, digits).padEnd(digits, "0")) + (Number(d.fraction[digits] || 0) >= 5 ? 1n : 0n);
  return d.negative ? -magnitude : magnitude;
}
function render(value: number | string, currency: string, digits: number): string {
  const amount = rounded(value, digits); if (amount === undefined) return "—";
  const magnitude = amount < 0n ? -amount : amount;
  const scale = 10n ** BigInt(digits), whole = magnitude / scale;
  const signedWhole = amount < 0n ? (whole === 0n ? -0 : -whole) : whole;
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits })
    .formatToParts(signedWhole).map(part => part.type === "fraction" ? String(magnitude % scale).padStart(digits, "0") : part.value).join("");
}
export function formatMoney(value: number | string, currency = "USD"): string {
  return render(value, currency, currencyDecimals(currency));
}
/** Rates and per-unit prices retain meaningful extra digits; extended amounts use formatMoney. */
export function formatUnitPrice(value: number | string, currency = "USD"): string {
  return render(value, currency, Math.max(currencyDecimals(currency), decimal(value)?.fraction.replace(/0+$/, "").length || 0));
}
/** Reconciliation must expose recorded fractions that ordinary currency display rounds away. */
export function formatReconciliationAmount(value: number | string, currency = "USD"): string {
  const display = formatMoney(value, currency), exact = formatUnitPrice(value, currency);
  return display === exact ? display : `${display} (exact ${exact})`;
}
export function priceInputValue(value: number | string, currency = "USD"): string {
  const d = decimal(value); if (!d) return String(value);
  const fraction = d.fraction.replace(/0+$/, "").padEnd(currencyDecimals(currency), "0");
  return `${d.negative ? "-" : ""}${d.whole}${fraction ? "." + fraction : ""}`;
}
/** Extend at full precision, round each invoice line once, and then sum currency units. */
export function invoiceSubtotal(lines: { quantity: number | string; unitPrice: number | string }[], currency = "USD"): string {
  const digits = currencyDecimals(currency);
  const total = lines.reduce((sum, line) => {
    const q = decimal(line.quantity), p = decimal(line.unitPrice); if (!q || !p) return sum;
    const product = BigInt(q.whole + q.fraction) * BigInt(p.whole + p.fraction);
    const scale = q.fraction.length + p.fraction.length;
    const raw = product.toString().padStart(scale + 1, "0");
    const value = `${q.negative !== p.negative ? "-" : ""}${scale ? raw.slice(0, -scale) + "." + raw.slice(-scale) : raw}`;
    return sum + (rounded(value, digits) ?? 0n);
  }, 0n);
  const raw = (total < 0n ? -total : total).toString().padStart(digits + 1, "0");
  return `${total < 0n ? "-" : ""}${digits ? raw.slice(0, -digits) + "." + raw.slice(-digits) : raw}`;
}

/** Explain display rounding of historical/internal amounts without changing any ledger value. */
export function moneyDisplaySummary(values: (number | string)[], currency = "USD"): { total: string; adjustment: string } {
  const parts = values.map(decimal).filter((v): v is NonNullable<typeof v> => Boolean(v));
  const scale = Math.max(0, ...parts.map(p => p.fraction.length));
  const raw = parts.reduce((sum, p) => sum + BigInt(p.whole + p.fraction.padEnd(scale, "0")) * (p.negative ? -1n : 1n), 0n);
  function text(n: bigint, digits: number) {
    const value = (n < 0n ? -n : n).toString().padStart(digits + 1, "0");
    return `${n < 0n ? "-" : ""}${digits ? value.slice(0, -digits) + "." + value.slice(-digits) : value}`;
  }
  const digits = currencyDecimals(currency), total = rounded(text(raw, scale), digits) ?? 0n;
  const displayed = values.reduce<bigint>((sum, value) => sum + (rounded(value, digits) ?? 0n), 0n);
  return { total: text(total, digits), adjustment: text(total - displayed, digits) };
}
